/**
 * @voltxampere/ui — Viewport (Sprint 12)
 *
 * Per Blueprint §12.4. Owns the world ↔ screen affine
 * transform. The three free signals (`center`, `zoom`,
 * `rotation`) are Preact signals so any consumer downstream — the
 * scene derivation, the renderer scheduler, the inspector
 * mini-map — re-runs automatically when the camera moves.
 *
 * `zoomAt` implements the Photoshop / Figma anchor: the world
 * point under the cursor stays put while the surrounding canvas
 * scales around it. The naïve `zoom *= factor` form moves
 * everything toward the centre and feels broken at high zoom
 * differentials.
 *
 * Sprint 12 freezes `rotation` at 0 — the signal exists so the
 * Sprint 17 rotation tool can flip it on without an API change.
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { signal } from "@preact/signals-core";
export const ZOOM_MIN = 0.1;
export const ZOOM_MAX = 10;
const clamp = (n, min, max) => Math.max(min, Math.min(max, n));
export class Viewport {
    center = signal({ x: 0, y: 0 });
    zoom = signal(1);
    rotation = signal(0);
    worldToScreen(p, canvas) {
        const rect = canvas.getBoundingClientRect();
        const z = this.zoom.value;
        const c = this.center.value;
        return {
            x: (p.x - c.x) * z + rect.width / 2,
            y: (p.y - c.y) * z + rect.height / 2,
        };
    }
    screenToWorld(p, canvas) {
        const rect = canvas.getBoundingClientRect();
        const z = this.zoom.value;
        const c = this.center.value;
        return {
            x: (p.x - rect.width / 2) / z + c.x,
            y: (p.y - rect.height / 2) / z + c.y,
        };
    }
    /**
     * Snap a world point onto the nearest grid intersection. The
     * grid origin sits at the world origin; pass any positive
     * `gridSize`. Used by Sprint 13 wire / stamp tools.
     */
    snapToGrid(p, gridSize) {
        if (gridSize <= 0)
            return p;
        return {
            x: Math.round(p.x / gridSize) * gridSize,
            y: Math.round(p.y / gridSize) * gridSize,
        };
    }
    /**
     * Pan the camera by a screen-space delta. The screen delta is
     * divided by the current zoom so a 100-pixel drag at 4× zoom
     * moves the camera 25 world units — keeping the under-cursor
     * world point fixed.
     */
    pan(dxScreen, dyScreen) {
        const z = this.zoom.value;
        if (z === 0)
            return;
        const c = this.center.value;
        this.center.value = {
            x: c.x - dxScreen / z,
            y: c.y - dyScreen / z,
        };
    }
    /**
     * Multiply zoom by `factor`, clamped to [ZOOM_MIN, ZOOM_MAX],
     * keeping the world point under `screenPoint` stationary.
     */
    zoomAt(screenPoint, factor, canvas) {
        if (!Number.isFinite(factor) || factor <= 0)
            return;
        const before = this.screenToWorld(screenPoint, canvas);
        const next = clamp(this.zoom.value * factor, ZOOM_MIN, ZOOM_MAX);
        if (next === this.zoom.value)
            return;
        this.zoom.value = next;
        const after = this.screenToWorld(screenPoint, canvas);
        const c = this.center.value;
        this.center.value = {
            x: c.x + (before.x - after.x),
            y: c.y + (before.y - after.y),
        };
    }
    /** Recentre on origin, reset zoom to 1, leave rotation alone. */
    reset() {
        this.center.value = { x: 0, y: 0 };
        this.zoom.value = 1;
    }
    /** Frozen snapshot suitable for the immutable `Scene`. */
    snapshot() {
        return {
            center: { ...this.center.value },
            zoom: this.zoom.value,
            rotation: this.rotation.value,
        };
    }
}
//# sourceMappingURL=viewport.js.map